// Pembayaran Vendor — riwayat + panel detail (#4b-3b, layar 1)
//
// Layar BACA + satu aksi merusak (batalkan). Pencatatan pembayaran TIDAK ada
// di sini: pembayaran selalu lahir dari sebuah tagihan, jadi tombolnya ada di
// layar detail tagihan. Halaman ini menjawab pertanyaan yang berbeda —
// "uang apa saja yang sudah keluar, ke siapa, dengan bukti apa".
//
// NOL KONSTANTA BISNIS. Status, metode pembayaran, transisi yang tersedia,
// ambang panjang alasan, dan aturan bukti dibaca dari GET /meta/payment.
//
// BUKTI TRANSFER — keputusan sadar, jangan dibalik tanpa alasan:
//   * Lencana "Ada bukti" ditentukan ada/tidaknya proof_file_id yang memang
//     ikut di setiap baris daftar.
//   * Signed URL-nya TIDAK ikut di daftar. Ia diambil ON-DEMAND lewat
//     GET /payments/{id} saat pengguna menekan "Buka bukti" — 50 baris di
//     layar berarti 50 panggilan storage kalau daftar ikut menerbitkannya,
//     untuk tautan yang 49 di antaranya tidak pernah diklik.
//   * proof_file_id NULL bukan pelanggaran: 12 pembayaran lama sah tidak
//     punya bukti (Q49 memasang constraint NOT VALID justru supaya baris
//     lama tidak dibekukan). Karena itu tone-nya NETRAL, bukan danger.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { StatusBadge, MetaStatusBadge } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Banknote, RefreshCw, ExternalLink, FileWarning, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, formatDateTime } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import { bisaAksiMeta, transisiMeta } from "./VendorInvoiceDetail";
import Hint from "../../components/etams/Hint";
import {
  listPayments, getPayment, cancelPayment, getPaymentMeta, apiErrorMessage,
} from "../../lib/purchasingApi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import { hariLalu, saringanDari, saringanSampai } from "../../lib/saringanPurchasing";

//: Cermin `URUT_SAH_PAYMENT` di backend/services/vendor_payment_service.py.
const URUT_SAH = ["payment_date", "payment_number", "amount", "status", "created_at"];
//: Periode bawaan riwayat pembayaran — 90 hari terakhir (chip, bisa dihapus).
const HARI_BAWAAN = 90;

//: Lencana statusnya sendiri dirender <MetaStatusBadge> — komponen bersama
//: yang sudah menerjemahkan tone backend. Yang tidak disediakannya cuma
//: penjelasan status, jadi hanya itu yang diambil di sini.
export function deskripsiStatus(meta, code) {
  return (meta?.statuses || []).find(x => x.code === code)?.description || "";
}

/** Label metode pembayaran dari meta; kode apa adanya kalau belum termuat. */
export function labelMetode(meta, code) {
  return (meta?.payment_methods || []).find(m => m.code === code)?.label
    || code || "—";
}

/** Lencana bukti — lihat catatan panjang di kepala berkas. */
export function badgeBukti(pay) {
  return pay?.proof_file_id
    ? { tone: "success", label: "Ada bukti" }
    : { tone: "neutral", label: "Tanpa bukti (data lama)" };
}

export default function VendorPayments() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { tzForRegion } = useStoreTimezones();

  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  //: Nomor tagihan untuk chip saringan `invoice_id` — dari baris yang tampil.
  const [nomorTagihan, setNomorTagihan] = useState("");

  // POLA DAFTAR SERAGAM (28 Sep 2026; docs/POLA_DAFTAR.md). Saringan bawaan: 90 hari
  // terakhir. `invoice_id` datang dari layar tagihan (?invoice_id=) dan kini jadi
  // saringan biasa: terlihat sebagai chip, bisa dihapus, ikut tersimpan di alamat.
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "payment_date", arah: "desc" },
    saringan: {
      status: { label: "Status", tampil: (v) => (meta?.statuses || []).find(s => s.code === v)?.label || v },
      metode: { label: "Metode", param: "payment_method", tampil: (v) => labelMetode(meta, v) },
      invoice_id: { label: "Tagihan", tampil: () => nomorTagihan || "satu tagihan" },
      dari: saringanDari(hariLalu(HARI_BAWAAN)),
      sampai: saringanSampai(),
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan, nilaiSaringan } = daftar;
  const statusFilter = nilaiSaringan.status;
  const invoiceIdParam = nilaiSaringan.invoice_id;
  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listPayments(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const [detail, setDetail] = useState(null);      // baris hasil getPayment
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [batalOpen, setBatalOpen] = useState(false);
  const [reason, setReason] = useState("");
  //: id yang sedang diambil signed URL-nya, supaya tombolnya saja yang
  //: berputar — bukan seluruh tabel.
  const [bukaBukti, setBukaBukti] = useState(null);

  useEffect(() => {
    getPaymentMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat info pembayaran.")));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listPayments(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      const inv0 = (r.data.items || [])[0]?.invoice;
      if (inv0?.internal_number) {
        setNomorTagihan(`${inv0.internal_number}${inv0.vendor?.name ? ` · ${inv0.vendor.name}` : ""}`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat riwayat pembayaran."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  const bukaDetail = useCallback(async (pid) => {
    setDetailLoading(true);
    setDetail({ id: pid });
    try {
      const r = await getPayment(pid);
      setDetail(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat detail pembayaran."));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  //: ?open=<id> — jalan masuk dari layar tagihan supaya tautannya bisa
  //: dibagikan/di-refresh tanpa kehilangan panel yang sedang dibuka.
  const openParam = sp.get("open") || "";
  useEffect(() => {
    if (openParam) bukaDetail(openParam);
  }, [openParam, bukaDetail]);

  const tutupDetail = () => {
    setDetail(null);
    if (sp.get("open")) {
      const next = new URLSearchParams(sp);
      next.delete("open");
      setSp(next, { replace: true });
    }
  };

  /** Buka bukti transfer: signed URL diminta saat itu juga, tidak disimpan. */
  const lihatBukti = async (pid) => {
    setBukaBukti(pid);
    try {
      const r = await getPayment(pid);
      const url = r.data?.proof?.signed_url;
      if (!url) {
        toast.error(
          "Bukti transfer tidak bisa dibuka sekarang. Coba lagi sebentar lagi "
          + "atau hubungi Owner kalau berkasnya memang hilang."
        );
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyiapkan tautan bukti transfer."));
    } finally {
      setBukaBukti(null);
    }
  };

  const adaFilter = !!daftar.cari.trim() || daftar.saringanAktif.length > 0;
  const KOLOM_CSV = [
    { kunci: "payment_number", judul: "Nomor" },
    { kunci: "payment_date", judul: "Tanggal", ambil: (p) => formatDate(p.payment_date) },
    { kunci: "invoice", judul: "Tagihan", ambil: (p) => p.invoice?.internal_number || "" },
    { kunci: "vendor", judul: "Vendor", ambil: (p) => p.invoice?.vendor?.name || "" },
    { kunci: "payment_method", judul: "Metode", ambil: (p) => labelMetode(meta, p.payment_method) },
    { kunci: "amount", judul: "Nominal" },
    { kunci: "bukti", judul: "Bukti", ambil: (p) => badgeBukti(p).label },
    { kunci: "status", judul: "Status", ambil: (p) => (meta?.statuses || []).find(s => s.code === p.status)?.label || p.status },
  ];

  const inv = detail?.invoice || null;
  const tzDetail = tzForRegion(inv?.region_id || inv?.region?.id);
  const bolehBatal = detail?.status
    ? bisaAksiMeta(meta, "cancel", detail.status) : false;
  const minAlasan = transisiMeta(meta, "cancel")?.min_reason_length || 3;
  const alasanKurang = reason.trim().length < minAlasan;

  const jalankanBatal = async () => {
    setBusy(true);
    try {
      await cancelPayment(detail.id, { reason: reason.trim() });
      toast.success(
        "Pembayaran dibatalkan. Sisa tagihan dikembalikan otomatis."
      );
      setBatalOpen(false);
      setReason("");
      await bukaDetail(detail.id);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal membatalkan pembayaran."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pay-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Banknote className="h-6 w-6" /> Pembayaran Vendor
            <Hint>Pencatatan pembayaran dilakukan Finance dari layar tagihan yang bersangkutan.</Hint>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Riwayat uang yang benar-benar keluar ke vendor.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="pay-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
        </Button>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor pembayaran, nomor tagihan, atau vendor…" testId="pay-palang"
                    ekspor={{ nama: "pembayaran_vendor", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={statusFilter} data-testid="pay-status-filter" aria-label="Saring status"
                onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Semua status</option>
          {(meta?.statuses || []).map(s =>
            <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
        <select value={nilaiSaringan.metode} data-testid="pay-method-filter" aria-label="Saring metode"
                onChange={(e) => daftar.setSaringan("metode", e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Semua metode</option>
          {(meta?.payment_methods || []).map(m =>
            <option key={m.code} value={m.code}>{m.label}</option>)}
        </select>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          Dari
          <Input type="date" value={nilaiSaringan.dari} data-testid="pay-date-from"
                 className="h-9 w-40" onChange={(e) => daftar.setSaringan("dari", e.target.value)} />
        </label>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          sampai
          <Input type="date" value={nilaiSaringan.sampai} data-testid="pay-date-to"
                 className="h-9 w-40" onChange={(e) => daftar.setSaringan("sampai", e.target.value)} />
        </label>
      </PalangDaftar>
      {invoiceIdParam && (
        <p className="text-xs text-muted-foreground -mt-4" data-testid="pay-invoice-chip">
          Disaring untuk satu tagihan{nomorTagihan ? ` (${nomorTagihan})` : ""}.
        </p>
      )}
      {statusFilter && (
        <p className="text-xs text-muted-foreground -mt-4" data-testid="pay-status-help">
          {deskripsiStatus(meta, statusFilter)}
        </p>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2" data-testid="pay-loading">
            {[...Array(5)].map((_, i) =>
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
          </div>
        ) : daftar.tampil.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm space-y-2"
               data-testid="pay-empty">
            {adaFilter ? (
              <p>
                Tidak ada pembayaran yang cocok dengan pencarian atau saringan ini.
                Coba hapus saringannya — pembayaran lama ada di luar 90 hari terakhir.
              </p>
            ) : (
              <>
                <p>
                  Belum ada pembayaran ke vendor. Pembayaran dicatat Finance
                  dari sebuah tagihan yang sudah disetujui Kepala Cabang/Owner.
                </p>
                <Button variant="outline" size="sm" data-testid="pay-empty-goto-inv"
                        onClick={() => navigate("/purchasing/invoices")}>
                  Lihat Tagihan Vendor
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <ThUrut daftar={daftar} kunci="payment_number" className="px-4 py-3 font-medium">Nomor</ThUrut>
                  <ThUrut daftar={daftar} kunci="payment_date" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                  <th className="px-4 py-3 text-left font-medium">Tagihan</th>
                  <th className="px-4 py-3 text-left font-medium">Vendor</th>
                  <th className="px-4 py-3 text-left font-medium">Metode</th>
                  <ThUrut daftar={daftar} kunci="amount" align="right" className="px-4 py-3 font-medium">Nominal</ThUrut>
                  <th className="px-4 py-3 text-left font-medium">Bukti</th>
                  <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                </tr>
              </thead>
              <tbody className="divide-y">
                {daftar.tampil.map(p => {
                  const bb = badgeBukti(p);
                  const tz = tzForRegion(p.invoice?.region_id || p.invoice?.region?.id);
                  return (
                    <tr key={p.id} className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => bukaDetail(p.id)}
                        data-testid={`pay-row-${p.payment_number}`}>
                      <td className="px-4 py-3 font-medium">{p.payment_number}</td>
                      <td className="px-4 py-3">{formatDate(p.payment_date, tz)}</td>
                      <td className="px-4 py-3">{p.invoice?.internal_number || "—"}</td>
                      <td className="px-4 py-3">{p.invoice?.vendor?.name || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {labelMetode(meta, p.payment_method)}
                      </td>
                      <td className="px-4 py-3 text-right">{formatIDR(p.amount)}</td>
                      <td className="px-4 py-3">
                        {p.proof_file_id ? (
                          <Button variant="ghost" size="sm" className="h-7 px-2"
                                  disabled={bukaBukti === p.id}
                                  data-testid={`pay-proof-${p.payment_number}`}
                                  onClick={(e) => { e.stopPropagation(); lihatBukti(p.id); }}>
                            {bukaBukti === p.id
                              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              : <ExternalLink className="h-3.5 w-3.5 mr-1" />}
                            Buka bukti
                          </Button>
                        ) : (
                          <StatusBadge tone={bb.tone}>{bb.label}</StatusBadge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <MetaStatusBadge meta={meta} code={p.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Paginasi daftar={daftar} testId="pay-paginasi" />
      </Card>

      {/* --------------------------- Panel detail -------------------------- */}
      {/* Dialog konfirmasi pembatalan bertumpuk DI ATAS panel ini. Panel
          sengaja tidak ikut tertutup selama dialog itu terbuka — kalau ikut
          tertutup, konteks nominal & vendor yang sedang dikonfirmasi hilang
          dari layar tepat saat dibutuhkan. */}
      <Dialog open={!!detail}
              onOpenChange={(o) => !busy && !batalOpen && !o && tutupDetail()}>
        <DialogContent className="max-w-2xl" data-testid="pay-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              {detail?.payment_number || "Detail pembayaran"}
              {detail?.status && (
                <MetaStatusBadge meta={meta} code={detail.status}
                                 testId="pay-detail-status" />
              )}
            </DialogTitle>
            <DialogDescription>
              {deskripsiStatus(meta, detail?.status)
                || "Rincian satu pembayaran ke vendor."}
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="space-y-2" data-testid="pay-detail-loading">
              {[...Array(4)].map((_, i) =>
                <div key={i} className="h-10 bg-muted rounded animate-pulse" />)}
            </div>
          ) : !detail?.payment_number ? (
            <p className="text-sm text-muted-foreground" data-testid="pay-detail-empty">
              Pembayaran tidak ditemukan — mungkin sudah dihapus, atau bukan
              bagian cabang Anda.
            </p>
          ) : (
            <div className="space-y-4">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-muted-foreground text-xs">Tagihan</dt>
                  <dd>
                    {inv?.id ? (
                      <button type="button" data-testid="pay-detail-inv-link"
                              className="font-medium underline underline-offset-2 hover:text-foreground/80"
                              onClick={() => navigate(`/purchasing/invoices/${inv.id}`)}>
                        {inv.internal_number}
                      </button>
                    ) : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Vendor</dt>
                  <dd className="font-medium">{inv?.vendor?.name || "—"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Tanggal bayar</dt>
                  <dd>{formatDate(detail.payment_date, tzDetail)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Metode</dt>
                  <dd>{labelMetode(meta, detail.payment_method)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Nominal</dt>
                  <dd className="text-lg font-semibold" data-testid="pay-detail-amount">
                    {formatIDR(detail.amount)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Nilai tagihan</dt>
                  <dd>{formatIDR(inv?.total_amount)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Referensi bank</dt>
                  <dd>{detail.bank_ref || <span className="text-muted-foreground">—</span>}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Wilayah</dt>
                  <dd>{inv?.region?.name || "—"}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-muted-foreground text-xs">Dicatat</dt>
                  <dd>{formatDateTime(detail.created_at, tzDetail)}</dd>
                </div>
                {detail.notes && (
                  <div className="col-span-2">
                    <dt className="text-muted-foreground text-xs">Catatan</dt>
                    <dd>{detail.notes}</dd>
                  </div>
                )}
              </dl>

              {/* Bukti transfer */}
              <div className="rounded border border-border p-3" data-testid="pay-detail-proof">
                <div className="text-xs text-muted-foreground mb-2">Bukti transfer</div>
                {detail.proof?.signed_url ? (
                  <div className="flex items-center justify-between gap-2 flex-wrap text-sm">
                    <span className="text-muted-foreground">
                      Diunggah {formatDateTime(detail.proof.uploaded_at, tzDetail)}
                      {detail.proof.mime_type ? ` · ${detail.proof.mime_type}` : ""}
                    </span>
                    <a href={detail.proof.signed_url} target="_blank"
                       rel="noopener noreferrer" data-testid="pay-detail-proof-link"
                       className="inline-flex items-center gap-1 underline underline-offset-2">
                      <ExternalLink className="h-4 w-4" /> Buka bukti
                      {detail.proof.expires_in_sec
                        ? ` (berlaku ${Math.round(detail.proof.expires_in_sec / 60)} menit)`
                        : ""}
                    </a>
                  </div>
                ) : detail.proof_file_id ? (
                  <p className="text-sm text-muted-foreground">
                    Bukti tercatat tapi tautannya gagal disiapkan. Tutup lalu
                    buka lagi detail ini; kalau tetap gagal, laporkan ke Owner.
                  </p>
                ) : (
                  <div className="flex items-center gap-2 text-sm">
                    <StatusBadge tone="neutral" testId="pay-detail-no-proof">
                      Tanpa bukti (data lama)
                    </StatusBadge>
                    <span className="text-muted-foreground">
                      Pembayaran ini dicatat sebelum bukti transfer diwajibkan.
                    </span>
                  </div>
                )}
              </div>

              {/* Jejak pembatalan */}
              {detail.cancelled_at && (
                <div className="flex items-start gap-2 rounded border border-danger/40
                                bg-danger-soft p-3 text-sm text-danger-soft-foreground"
                     data-testid="pay-detail-cancelled">
                  <FileWarning className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Dibatalkan pada {formatDateTime(detail.cancelled_at, tzDetail)}.
                    {detail.cancel_reason && (
                      <span className="block">Alasan: {detail.cancel_reason}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={tutupDetail} disabled={busy}>
              Tutup
            </Button>
            {/* bisaAksiMeta sudah memeriksa permissions DAN status asal —
                tidak perlu ditambah pemeriksaan permissions kedua di sini. */}
            {bolehBatal && (
              <BusyButton variant="destructive" busy={busy}
                          data-testid="pay-cancel-btn"
                          onClick={() => { setReason(""); setBatalOpen(true); }}>
                Batalkan Pembayaran
              </BusyButton>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------- Dialog batalkan (uang) --------------------- */}
      <Dialog open={batalOpen} onOpenChange={(o) => !busy && setBatalOpen(o)}>
        <DialogContent data-testid="pay-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Batalkan pembayaran</DialogTitle>
            <DialogDescription>
              Pembayaran {detail?.payment_number} senilai{" "}
              <strong>{formatIDR(detail?.amount)}</strong> ke{" "}
              <strong>{inv?.vendor?.name || "vendor ini"}</strong> akan
              dibatalkan. Nilai itu dikembalikan ke sisa tagihan{" "}
              {inv?.internal_number || ""} dan statusnya dihitung ulang.
              Pembatalan tidak menarik kembali uang yang sudah ditransfer —
              itu urusan di luar sistem.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Alasan</label>
            <Textarea value={reason} rows={3} data-testid="pay-cancel-reason"
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Tuliskan alasannya supaya bisa ditelusuri." />
            <p className="text-xs text-muted-foreground">
              Minimal {minAlasan} karakter.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatalOpen(false)} disabled={busy}>
              Batal
            </Button>
            <BusyButton variant="destructive" busy={busy} busyText="Membatalkan..."
                        disabled={alasanKurang} data-testid="pay-cancel-confirm"
                        onClick={jalankanBatal}>
              Batalkan Pembayaran
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
